import { keyframes } from '@emotion/react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { COLORS, ShimmerLine, ShimmerValue, Tip } from '@/components/dashboard/scoring';
import { CardMode } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { Issue, IssueUrgency, urgencyOrder } from '@/models/Issue';
import { VALUE_FONT_SMALL } from '@/theme/consts';
import { useTranslationPrefix } from '@/translations/i18n';
import { getAverageIssueCost } from '@/utils/issues';

export interface InspectionSummaryVisualCardProps {
  listPrice?: number;
  cardMode?: CardMode;
  onIssueSelect?: (issue: Issue) => void;
  hideRepairBurden?: boolean;
  // When provided, the "See inspection details" link calls this instead of
  // navigating to the inspection report route. Used to open the carousel
  // overlay from HomeHQ.
  onSeeDetailsClick?: () => void;
  /** 27" Overview: the HomeDNA strip gets taller ticks (the score cards above give up the same room). */
  tall?: boolean;
}

const URGENCY_CHIP_COLORS: Record<IssueUrgency, string> = {
  [IssueUrgency.Critical]: COLORS.critical,
  [IssueUrgency.Urgent]: COLORS.urgent,
  [IssueUrgency.Moderate]: COLORS.moderate,
  [IssueUrgency.Low]: COLORS.low,
  [IssueUrgency.Monitor]: COLORS.monitor,
};

const formatCostShort = (cost: number) => {
  if (cost >= 1_000_000) return `$${(cost / 1_000_000).toFixed(1)}M`;
  if (cost >= 1_000) return `$${Math.round(cost / 1_000)}K`;
  return `$${cost.toLocaleString()}`;
};

const UrgencyChip = ({
  urgency,
  count,
  costRange,
}: {
  urgency: IssueUrgency;
  count: number;
  costRange?: { min: number; max: number };
}) => {
  const [show, setShow] = useState(false);
  const [tipOffset, setTipOffset] = useState<number | null>(null);
  const chipRef = useRef<HTMLSpanElement>(null);

  const handleEnter = () => {
    if (chipRef.current) {
      const chip = chipRef.current.getBoundingClientRect();
      const card = chipRef.current.closest('.MuiCard-root') as HTMLElement | null;
      if (card) {
        const cardRect = card.getBoundingClientRect();
        const tipW = 220;
        const chipCenter = chip.left + chip.width / 2;
        let tipLeft = chipCenter - tipW / 2;
        if (tipLeft < cardRect.left + 12) tipLeft = cardRect.left + 12;
        if (tipLeft + tipW > cardRect.right - 12) tipLeft = cardRect.right - 12 - tipW;
        setTipOffset(tipLeft - chip.left);
      }
    }
    setShow(true);
  };

  const hasCost = costRange && (costRange.min > 0 || costRange.max > 0);

  return (
    <span
      ref={chipRef}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 0',
        fontSize: 11,
        fontWeight: 500,
        color: 'var(--tx-muted)',
        cursor: 'help',
        fontFamily: "'Manrope', sans-serif",
      }}>
      {/* the legend speaks the DNA's language: the group's hairline, its name, its count */}
      <span style={{ width: 2, height: 12, borderRadius: 1, background: URGENCY_CHIP_COLORS[urgency] }} />
      <span>{urgency}</span>
      <span style={{ color: 'var(--tx-text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
      {show && (
        <span
          style={{
            position: 'absolute',
            left: tipOffset != null ? tipOffset : '50%',
            top: '100%',
            transform: tipOffset != null ? 'none' : 'translateX(-50%)',
            marginTop: 6,
            background: 'var(--tx-glass)',
            backdropFilter: 'var(--tx-blur)',
            WebkitBackdropFilter: 'var(--tx-blur)',
            borderRadius: '12px',
            padding: '10px 14px',
            fontSize: 12,
            color: COLORS.text,
            lineHeight: 1.55,
            boxShadow: 'var(--tx-shadow-sm)',
            zIndex: 9999,
            pointerEvents: 'none',
            fontWeight: 400,
            letterSpacing: 0,
            textTransform: 'none' as const,
            textAlign: 'left' as const,
            whiteSpace: 'nowrap',
          }}>
          <div style={{ marginBottom: 4 }}>Estimated repair range:</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <rect x="4" y="2" width="16" height="20" rx="2" />
              <line x1="8" y1="6" x2="16" y2="6" />
              <line x1="8" y1="10" x2="16" y2="10" />
              <line x1="8" y1="14" x2="12" y2="14" />
            </svg>
            {hasCost
              ? `$${costRange.min.toLocaleString()} – $${costRange.max.toLocaleString()}`
              : '$0'}
          </div>
        </span>
      )}
    </span>
  );
};

const InspectionSummaryVisualCard = ({
  listPrice,
  cardMode: _cardMode = 'light',
  onIssueSelect,
  hideRepairBurden,
  onSeeDetailsClick,
  tall = false,
}: InspectionSummaryVisualCardProps) => {
  const t = useTranslationPrefix('home.inspection_summary_visual');
  const { selectedProperty, propertyIssues, isInitialFetchingPropertyIssues } =
    usePropertiesContext();
  const { propertyDetails } = useHauserEQResults(selectedProperty?.property.id);
  // Direct window-width check — useMediaQuery returns false on iOS Safari
  // despite viewport being <768px. Same pattern as InspectionPunchList.
  const [winWidth, setWinWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );
  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = winWidth <= 768;
  const navigate = useNavigate();

  // Resolve list price: prop > HauserEQ property_details
  const effectiveListPrice = listPrice ?? propertyDetails?.price;

  const [hoveredIssue, setHoveredIssue] = useState<Issue | null>(null);
  const [hoveredBarX, setHoveredBarX] = useState<number | null>(null);
  const barsContainerRef = useRef<HTMLDivElement>(null);

  const issues = propertyIssues ?? [];

  // Count issues by urgency
  const urgencyCounts = useMemo(() => {
    const counts = {
      [IssueUrgency.Critical]: 0,
      [IssueUrgency.Urgent]: 0,
      [IssueUrgency.Moderate]: 0,
      [IssueUrgency.Low]: 0,
      [IssueUrgency.Monitor]: 0,
    };
    issues.forEach((issue) => {
      if (!issue.resolved) {
        counts[issue.urgency]++;
      }
    });
    return counts;
  }, [issues]);

  // Cost ranges per urgency
  const urgencyCostRanges = useMemo(() => {
    const ranges: Record<string, { min: number; max: number }> = {};
    issues.forEach((issue) => {
      if (issue.resolved) return;
      const u = issue.urgency;
      const lo = issue.costEstimateMin ?? 0;
      const hi = issue.costEstimateMax ?? 0;
      if (!ranges[u]) ranges[u] = { min: 0, max: 0 };
      ranges[u].min += lo;
      ranges[u].max += hi;
    });
    return ranges;
  }, [issues]);

  // Sort issues by priority (Critical first, then Urgent, Moderate, Low, Monitor)
  const sortedIssues = useMemo(
    () =>
      [...issues]
        .filter((issue) => !issue.resolved)
        .sort((a, b) => urgencyOrder[a.urgency] - urgencyOrder[b.urgency]),
    [issues],
  );

  // Calculate totals
  const maxIssueCost = useMemo(
    () => sortedIssues.reduce((max, issue) => Math.max(max, getAverageIssueCost(issue)), 0),
    [sortedIssues],
  );
  const totalIssues = sortedIssues.length;
  const totalEstimatedCost = useMemo(
    () => sortedIssues.reduce((sum, issue) => sum + getAverageIssueCost(issue), 0),
    [sortedIssues],
  );

  const repairRatio = effectiveListPrice
    ? ((totalEstimatedCost / effectiveListPrice) * 100).toFixed(1)
    : null;

  const getUrgencyColor = (urgency: IssueUrgency) => URGENCY_CHIP_COLORS[urgency] ?? COLORS.monitor;

  const handleBarClick = (issue: Issue) => {
    if (onIssueSelect) {
      // Parent (e.g. HomeHQ) is taking over — open the carousel overlay
      // for this issue instead of navigating to the inspection report page.
      onIssueSelect(issue);
      return;
    }
    navigate('/dashboard/inspection-report', { state: { issueId: issue.id } });
  };

  if (isInitialFetchingPropertyIssues) {
    return (
      <Card sx={{ bgcolor: COLORS.card, borderRadius: '24px' }}>
        <CardContent sx={{ p: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px' }}>
            {/* Left section */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: isMobile ? 160 : 240,
              }}>
              {/* Header row */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: isMobile ? 1.5 : 0,
                }}>
                <Box>
                  <div style={{ fontSize: 17, fontWeight: 300, color: COLORS.text }}>
                    Home<b>DNA</b>
                    <sup style={{ fontSize: '0.5em' }}>™</sup>
                  </div>
                  <ShimmerLine width={100} height={14} style={{ marginTop: 4 }} />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '20px',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}>
                  {(
                    [
                      IssueUrgency.Critical,
                      IssueUrgency.Urgent,
                      IssueUrgency.Moderate,
                      IssueUrgency.Low,
                      IssueUrgency.Monitor,
                    ] as IssueUrgency[]
                  ).map((urgency) => (
                    <span
                      key={urgency}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '4px 12px',
                        borderRadius: 6,
                        background: `${URGENCY_CHIP_COLORS[urgency]}1a`,
                        color: URGENCY_CHIP_COLORS[urgency],
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: "'Manrope', sans-serif",
                      }}>
                      <span>{urgency}:</span>
                      <ShimmerValue
                        width={16}
                        height={13}
                        borderRadius={4}
                        style={{ backgroundColor: `${URGENCY_CHIP_COLORS[urgency]}33` }}
                      />
                    </span>
                  ))}
                </Box>
              </Box>
              {/* Shimmer bars */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  mt: 2,
                }}>
                <ShimmerLine width={260} height={12} style={{ marginBottom: 15, opacity: 0.6 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', height: '52px', gap: '3px' }}>
                  {Array.from({ length: 20 }).map((_, i) => (
                    <ShimmerLine
                      key={i}
                      width={`${3 + (i % 5) * 2}%`}
                      height="100%"
                      borderRadius={10}
                      delay={i * 0.05}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
            {/* Right stat card */}
            {!isMobile && (
              <Box
                sx={{
                  background: COLORS.cardAlt,
                  borderRadius: '16px',
                  padding: '20px',
                  minWidth: 180,
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '16px',
                  textAlign: 'center',
                }}>
                <Box>
                  <ShimmerValue width={48} height={28} />
                  <div style={{ fontSize: 11, color: COLORS.text, marginTop: 4 }}>
                    {t('num_remarks')}
                  </div>
                </Box>
                <Box>
                  <ShimmerValue width={90} height={28} />
                  <div style={{ fontSize: 11, color: COLORS.text, marginTop: 4 }}>
                    {t('total_estimated_costs')}
                  </div>
                </Box>
                <Box>
                  <ShimmerValue width={48} height={28} />
                  <div style={{ fontSize: 11, color: COLORS.text, marginTop: 4 }}>
                    {t('repair_burden')}
                  </div>
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        bgcolor: COLORS.card,
        borderRadius: '24px',
        transition: 'box-shadow 0.2s ease',
      }}>
      <CardContent sx={{ p: '20px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Full width section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              minWidth: 0,
            }}>
            {/* Header row — title + chips + stat card */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'flex-start',
                mb: isMobile ? '30px' : '12px',
                gap: isMobile ? '10px' : '14px',
              }}>
              <Box sx={{ flexShrink: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 300, color: COLORS.text }}>
                  Home<b>DNA</b>
                  <sup style={{ fontSize: '0.5em' }}>™</sup>
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onSeeDetailsClick) {
                      onSeeDetailsClick();
                    } else {
                      navigate('/dashboard/inspection-report');
                    }
                  }}
                  style={{
                    fontSize: 13,
                    color: 'var(--tx-muted)',
                    cursor: 'pointer',
                    marginTop: 4,
                    marginBottom: 15,
                  }}>
                  See inspection details &rarr;
                </div>
              </Box>
              {/* Urgency chips */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: isMobile ? 'flex-start' : 'flex-end',
                  gap: isMobile ? '8px' : '6px 20px',
                  flexWrap: 'wrap',
                }}>
                {(
                  [
                    IssueUrgency.Critical,
                    IssueUrgency.Urgent,
                    IssueUrgency.Moderate,
                    IssueUrgency.Low,
                    IssueUrgency.Monitor,
                  ] as IssueUrgency[]
                ).map((urgency) => (
                  <UrgencyChip
                    key={urgency}
                    urgency={urgency}
                    count={urgencyCounts[urgency]}
                    costRange={urgencyCostRanges[urgency]}
                  />
                ))}
              </Box>
              {/* Stat card — inline on desktop */}
              {!isMobile && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    marginLeft: 28,
                  }}>
                  <div style={{ padding: '0 0 0 20px', textAlign: 'right' }}>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 200,
                        color: 'var(--tx-text)',
                        lineHeight: 1,
                        letterSpacing: '-0.05em',
                        fontVariantNumeric: 'proportional-nums',
                        fontFamily: VALUE_FONT_SMALL,
                      }}>
                      {totalIssues}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'var(--tx-muted)',
                        marginTop: 6,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      Remarks
                    </div>
                  </div>
                  <div style={{ marginLeft: 20, padding: '0 0 0 20px', textAlign: 'right', borderLeft: '1px solid var(--tx-line)' }}>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 200,
                        color: 'var(--tx-text)',
                        lineHeight: 1,
                        letterSpacing: '-0.05em',
                        fontVariantNumeric: 'proportional-nums',
                        fontFamily: VALUE_FONT_SMALL,
                      }}>
                      ${totalEstimatedCost.toLocaleString()}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'var(--tx-muted)',
                        marginTop: 6,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      Est. cost
                    </div>
                  </div>
                  {repairRatio !== null && !hideRepairBurden && (
                    <div style={{ marginLeft: 20, padding: '0 0 0 20px', textAlign: 'right', borderLeft: '1px solid var(--tx-line)' }}>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 200,
                          color: 'var(--tx-text)',
                          lineHeight: 1,
                          letterSpacing: '-0.05em',
                          fontVariantNumeric: 'proportional-nums',
                          fontFamily: VALUE_FONT_SMALL,
                        }}>
                        {repairRatio}%
                      </div>
                      <Tip
                        text={
                          'Estimated repair cost as a percentage of list price. Under 5% is typical; ' +
                          '5–10% suggests significant deferred maintenance; above 10% indicates major renovation.'
                        }>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: 'var(--tx-muted)',
                            marginTop: 6,
                            cursor: 'help',
                            fontFamily: "'Manrope',sans-serif",
                          }}>
                          Repair burden
                        </div>
                      </Tip>
                    </div>
                  )}
                </div>
              )}
            </Box>

            {/* Bars */}
            <Box
              ref={barsContainerRef}
              sx={{
                mt: 'auto',
                position: 'relative',
                // Mobile-only: tighten spacing above + below the bars by 10px each.
                ...(isMobile && { mt: '-10px', mb: '-10px' }),
              }}>
              {/* Hover tooltip — floats above the bars, tracks hovered bar */}
              {hoveredIssue && !isMobile && hoveredBarX != null && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '70px',
                    left: `${Math.max(
                      0,
                      Math.min(hoveredBarX, (barsContainerRef.current?.offsetWidth ?? 280) - 280),
                    )}px`,
                    zIndex: 20,
                    background: 'var(--tx-glass)',
                    backdropFilter: 'var(--tx-blur)',
                    WebkitBackdropFilter: 'var(--tx-blur)',
                    boxShadow: 'var(--tx-shadow-sm)',
                    borderRadius: '12px',
                    p: '12px 14px',
                    width: 280,
                    pointerEvents: 'none',
                  }}>
                  {/* Eyebrow: the finding's hairline in its urgency colour, urgency · category */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'var(--tx-muted)',
                      marginBottom: 6,
                    }}>
                    <span
                      style={{
                        width: 2,
                        height: 12,
                        borderRadius: 1,
                        background: getUrgencyColor(hoveredIssue.urgency),
                      }}
                    />
                    <span>
                      {hoveredIssue.urgency} · {categoryLabel(hoveredIssue.category)}
                      {getAverageIssueCost(hoveredIssue) >= 5000 && (
                        <span style={{ color: COLORS.nhdAccent }}> · Big ticket</span>
                      )}
                    </span>
                  </div>
                  {/* Title */}
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      lineHeight: 1.35,
                      color: 'var(--tx-text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {hoveredIssue.title}
                  </div>
                  {/* The cost range as the figure, the estimate beside it as meta */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 12,
                      marginTop: 10,
                    }}>
                    {hoveredIssue.costEstimateMin != null || hoveredIssue.costEstimateMax != null ? (
                      <span
                        style={{
                          fontSize: 15,
                          fontWeight: 300,
                          letterSpacing: '-0.02em',
                          lineHeight: 1,
                          color: 'var(--tx-text)',
                        }}>
                        ${(hoveredIssue.costEstimateMin ?? 0).toLocaleString()}–$
                        {(hoveredIssue.costEstimateMax ?? 0).toLocaleString()}
                      </span>
                    ) : (
                      <span />
                    )}
                    <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>
                      Est. impact ${getAverageIssueCost(hoveredIssue).toLocaleString()}
                    </span>
                  </div>
                </Box>
              )}

              {isMobile ? (
                <SeveritySegmentBar
                  urgencyCounts={urgencyCounts}
                  urgencyCostRanges={urgencyCostRanges}
                  noIssuesText={t('no_issues')}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    height: tall ? '116px' : '56px',
                    borderBottom: '1px solid var(--tx-line)',
                  }}>
                  {sortedIssues.map((issue, index) => (
                    <IssueBar
                      key={issue.id}
                      issue={issue}
                      index={index}
                      count={sortedIssues.length}
                      maxCost={maxIssueCost}
                      color={getUrgencyColor(issue.urgency)}
                      isMobile={false}
                      isHovered={hoveredIssue?.id === issue.id}
                      isAnyHovered={hoveredIssue !== null}
                      onMouseEnter={(e: React.MouseEvent) => {
                        const barRect = e.currentTarget.getBoundingClientRect();
                        const containerRect = barsContainerRef.current?.getBoundingClientRect();
                        if (containerRect) {
                          const barCenterX = barRect.left + barRect.width / 2 - containerRect.left;
                          setHoveredBarX(barCenterX);
                        }
                        setHoveredIssue(issue);
                      }}
                      onMouseLeave={() => {
                        setHoveredIssue(null);
                        setHoveredBarX(null);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBarClick(issue);
                      }}
                    />
                  ))}
                  {sortedIssues.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      {t('no_issues')}
                    </Typography>
                  )}
                </Box>
              )}
              {!isMobile && sortedIssues.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 8,
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--tx-muted)',
                  }}>
                  <span>Critical</span>
                  <span style={{ color: 'var(--tx-faint)' }}>Each line is a finding · height is its estimated cost</span>
                  <span>Monitor</span>
                </div>
              )}
            </Box>

            {/* Mobile stats row (below bars) */}
            {isMobile && (
              <Box sx={{ mt: '20px', mb: '-10px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    py: '10px',
                    borderTop: '1px solid var(--tx-line)',
                  }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-muted)' }}># of remarks</span>
                  <span
                    style={{
                      fontSize: 17,
                      fontWeight: 300,
                      letterSpacing: '-0.02em',
                      fontVariantNumeric: 'tabular-nums',
                      color: 'var(--tx-text)',
                    }}>
                    {totalIssues}
                  </span>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    py: '10px',
                    borderTop: '1px solid var(--tx-line)',
                  }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-muted)' }}>Total est. cost</span>
                  <span
                    style={{
                      fontSize: 17,
                      fontWeight: 300,
                      letterSpacing: '-0.02em',
                      fontVariantNumeric: 'tabular-nums',
                      color: 'var(--tx-text)',
                    }}>
                    {formatCostShort(totalEstimatedCost)}
                  </span>
                </Box>
                {repairRatio !== null && !hideRepairBurden && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      py: '10px',
                      borderTop: '1px solid var(--tx-line)',
                    }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-muted)' }}>Repair / price</span>
                    <span
                      style={{
                        fontSize: 17,
                        fontWeight: 300,
                        letterSpacing: '-0.02em',
                        fontVariantNumeric: 'tabular-nums',
                        color: 'var(--tx-text)',
                      }}>
                      {repairRatio}%
                    </span>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// A finding's category as a label: sentence case with acronyms kept ("ELECTRICAL" -> "Electrical", "HVAC" stays)
const CATEGORY_ACRONYMS = new Set(['HVAC', 'HOA', 'NHD', 'ADU']);
const categoryLabel = (raw: string) =>
  raw
    .replace(/_/g, ' ')
    .split(' ')
    .map((w, i) =>
      CATEGORY_ACRONYMS.has(w.toUpperCase())
        ? w.toUpperCase()
        : i === 0
          ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
          : w.toLowerCase(),
    )
    .join(' ');

interface IssueBarProps {
  issue: Issue;
  index: number;
  count: number;
  maxCost: number;
  color: string;
  isHovered: boolean;
  isAnyHovered: boolean;
  isMobile: boolean;
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onClick: (e: React.MouseEvent) => void;
}

// One finding as a hairline tick (the Punch List tile's language, made technical): an equal share of the width as its
// hit area, a 2px line in its urgency colour, height by estimated cost on a square-root scale so small items still read
// On load a wave ripples through the strand left to right: each line rises from the baseline, overshoots a touch and
// settles, a few milliseconds after its neighbour
const tickRise = keyframes`
  0% { transform: scaleY(0); }
  60% { transform: scaleY(1.18); }
  100% { transform: scaleY(1); }
`;
const WAVE_MS = 900;

const IssueBar = ({
  issue,
  index,
  count,
  maxCost,
  color,
  isHovered,
  isAnyHovered,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: IssueBarProps) => {
  const avgCost = getAverageIssueCost(issue);
  const heightPct = maxCost > 0 ? 16 + 84 * Math.sqrt(avgCost / maxCost) : 16;

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        height: '100%',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        cursor: 'pointer',
      }}>
      <Box
        sx={{
          width: isHovered ? 3 : 2,
          height: `${heightPct}%`,
          bgcolor: color,
          borderRadius: '1px 1px 0 0',
          opacity: isAnyHovered ? (isHovered ? 1 : 0.22) : 0.9,
          transition: 'opacity 0.15s, width 0.15s',
          transformOrigin: 'bottom',
          animation: `${tickRise} 520ms cubic-bezier(0.22, 1, 0.36, 1) ${Math.round((index / Math.max(count, 1)) * WAVE_MS)}ms both`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    </Box>
  );
};

// ─── Mobile: 5-segment severity bar ──────────────────────────────────────
// Replaces the per-issue pill grid on mobile. Each segment = one severity
// level, width proportional to that group's total cost. Tap to reveal a
// tooltip with urgency / remark count / cost range. No click-through to
// individual issues on mobile.

const SEVERITY_PRIORITY: IssueUrgency[] = [
  IssueUrgency.Critical,
  IssueUrgency.Urgent,
  IssueUrgency.Moderate,
  IssueUrgency.Low,
  IssueUrgency.Monitor,
];

const SeveritySegmentBar = ({
  urgencyCounts,
  urgencyCostRanges,
  noIssuesText,
}: {
  urgencyCounts: Record<IssueUrgency, number>;
  urgencyCostRanges: Record<string, { min: number; max: number }>;
  noIssuesText: string;
}) => {
  const [selected, setSelected] = useState<IssueUrgency | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close tooltip on any tap outside the bar/tooltip.
  useEffect(() => {
    if (!selected) return;
    const handler = (e: Event) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setSelected(null);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [selected]);

  const segments = useMemo(
    () =>
      SEVERITY_PRIORITY.map((urgency) => {
        const count = urgencyCounts[urgency] ?? 0;
        const range = urgencyCostRanges[urgency] ?? { min: 0, max: 0 };
        const avgCost = (range.min + range.max) / 2;
        return {
          urgency,
          count,
          range,
          avgCost,
          color: URGENCY_CHIP_COLORS[urgency],
        };
      }).filter((s) => s.count > 0),
    [urgencyCounts, urgencyCostRanges],
  );

  const totalCount = segments.reduce((s, seg) => s + seg.count, 0);

  if (segments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
        {noIssuesText}
      </Typography>
    );
  }

  // Count-weighted (matches the old mobile bar's visual where each issue
  // was equal-width, so per-severity widths read as count-proportional).
  const widthFor = (count: number) =>
    totalCount > 0 ? (count / totalCount) * 100 : 100 / segments.length;

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          // a slim severity strip (the desktop's hairline grammar) inside a full-height tap target
          height: '48px',
          gap: '3px',
          width: '100%',
        }}>
        {segments.map((seg) => {
          const widthPct = widthFor(seg.count);
          const isSelected = selected === seg.urgency;
          return (
            <Box
              key={seg.urgency}
              onClick={(e) => {
                e.stopPropagation();
                setSelected(isSelected ? null : seg.urgency);
              }}
              sx={{
                width: `${widthPct}%`,
                height: '8px',
                bgcolor: seg.color,
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--tx-text)',
                fontSize: 11,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                transition: 'opacity 0.15s, filter 0.15s',
                opacity: selected ? (isSelected ? 1 : 0.45) : 1,
                filter: isSelected ? 'brightness(1.05)' : 'none',
              }}></Box>
          );
        })}
      </Box>
      {selected &&
        (() => {
          const seg = segments.find((s) => s.urgency === selected);
          if (!seg) return null;
          const hasCost = seg.range.min > 0 || seg.range.max > 0;
          return (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'var(--tx-glass)',
                backdropFilter: 'var(--tx-blur)',
                WebkitBackdropFilter: 'var(--tx-blur)',
                borderRadius: '12px',
                padding: '14px 16px',
                width: 'calc(100% - 8px)',
                maxWidth: 360,
                boxShadow: 'var(--tx-shadow-sm)',
                zIndex: 10,
              }}>
              <Box
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: seg.color,
                  marginBottom: '6px',
                }}>
                {seg.urgency}
              </Box>
              <Box
                sx={{
                  fontSize: 13,
                  color: COLORS.text,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                }}>
                <span>
                  {seg.count} remark{seg.count !== 1 ? 's' : ''}
                </span>
                <span style={{ color: COLORS.textDim }}>
                  {hasCost
                    ? `$${seg.range.min.toLocaleString()} – $${seg.range.max.toLocaleString()}`
                    : '—'}
                </span>
              </Box>
            </Box>
          );
        })()}
    </Box>
  );
};

export default InspectionSummaryVisualCard;
